import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Bell, Sparkles, Send, AlertTriangle } from "lucide-react";
import { API } from "@/lib/api";


export default function NudgeCenterPage() {
  const { authHeaders } = useAuth();
  const [atRisk, setAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [sending, setSending] = useState({});
  const [messages, setMessages] = useState({});
  const [sentStatus, setSentStatus] = useState({});

  useEffect(() => {
    axios.get(`${API}/nudge/at-risk`, { headers: authHeaders() })
      .then((r) => setAtRisk(r.data))
      .finally(() => setLoading(false));
  }, []);

  const handleGenerate = async (customer) => {
    setGenerating((g) => ({ ...g, [customer.id]: true }));
    try {
      const { data } = await axios.post(
        `${API}/nudge/generate`,
        { customer_id: customer.id, client_id: customer.client_id },
        { headers: authHeaders() }
      );
      setMessages((m) => ({ ...m, [customer.id]: data.message }));
    } catch (err) {
      setMessages((m) => ({ ...m, [customer.id]: "Failed to generate message. Please try again." }));
    } finally {
      setGenerating((g) => ({ ...g, [customer.id]: false }));
    }
  };

  const handleSend = async (customer) => {
    const msg = messages[customer.id];
    if (!msg) return;
    setSending((s) => ({ ...s, [customer.id]: true }));
    try {
      const { data } = await axios.post(
        `${API}/nudge/send`,
        { customer_id: customer.id, client_id: customer.client_id, message: msg },
        { headers: authHeaders() }
      );
      setSentStatus((s) => ({ ...s, [customer.id]: data.status }));
    } catch (err) {
      setSentStatus((s) => ({ ...s, [customer.id]: "failed" }));
    } finally {
      setSending((s) => ({ ...s, [customer.id]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="nudge-center-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Nudge Center</h1>
        <p className="text-sm text-ink/50 mt-0.5">
          {atRisk.length} at-risk customers — 80%+ of reward threshold, inactive 20+ days
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48 text-ink/40">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading at-risk customers…
        </div>
      ) : atRisk.length === 0 ? (
        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardContent className="py-16 flex flex-col items-center justify-center text-center">
            <Bell size={32} className="text-ink/30 mb-3" />
            <p className="text-ink/50 font-medium">No at-risk customers</p>
            <p className="text-xs text-ink/40 mt-1">All customers are active or below the 80% threshold</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {atRisk.map((customer) => (
            <Card key={customer.id} className="bg-white border border-ink/10 shadow-sm rounded-2xl" data-testid={`at-risk-customer-${customer.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-ink">{customer.name || "Unknown"}</p>
                      {customer.days_inactive >= 20 && (
                        <Badge variant="outline" className="text-xs bg-purple-50 text-ink border-warning/40 gap-1">
                          <AlertTriangle size={10} /> {customer.days_inactive}d inactive
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs bg-surface text-ink/60">{customer.vendor_name}</Badge>
                    </div>
                    <p className="text-xs text-ink/40 mb-2">{customer.email || customer.mobile_number}</p>
                    <div className="flex items-center gap-2">
                      <Progress value={customer.progress_percent} className="flex-1 h-1.5" />
                      <span className="text-xs font-mono text-ink/50 w-24 text-right">
                        {customer.point_balance}/{customer.threshold} pts ({customer.progress_percent}%)
                      </span>
                    </div>
                    <p className="text-xs text-ink/40 mt-1">
                      £{customer.reward_value_gbp} reward · {customer.fcm_endpoint ? "FCM configured" : "No FCM endpoint"}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleGenerate(customer)}
                      disabled={generating[customer.id]}
                      data-testid={`generate-nudge-${customer.id}`}
                      className="h-8 text-xs gap-1.5"
                    >
                      {generating[customer.id] ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      Generate
                    </Button>
                  </div>
                </div>

                {messages[customer.id] && (
                  <div className="mt-4 space-y-2">
                    <label className="text-xs font-semibold text-ink/50 uppercase tracking-widest">AI-Generated Message</label>
                    <Textarea
                      value={messages[customer.id]}
                      onChange={(e) => setMessages((m) => ({ ...m, [customer.id]: e.target.value }))}
                      className="text-sm resize-none h-20 font-mono"
                      data-testid={`nudge-message-${customer.id}`}
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSend(customer)}
                        disabled={sending[customer.id]}
                        data-testid={`send-nudge-${customer.id}`}
                        className="h-8 text-xs gap-1.5 bg-purple-500 hover:bg-purple-600 text-white"
                      >
                        {sending[customer.id] ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                        {sending[customer.id] ? "Sending…" : "Send via FCM"}
                      </Button>
                      {sentStatus[customer.id] && (
                        <Badge
                          variant="outline"
                          className={`text-xs ${sentStatus[customer.id] === "sent" ? "bg-purple-100 text-success border-success/30" : "bg-danger/10 text-danger border-danger/30"}`}
                          data-testid={`nudge-status-${customer.id}`}
                        >
                          {sentStatus[customer.id] === "sent" ? "Sent" : sentStatus[customer.id] === "no_endpoint" ? "No FCM endpoint" : "Failed"}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
